// Array & object utilities — extracted from MyScripts/Utils.ts

import { hasValue } from './validation';

export function orderBy(arr: any[], fields: string[], directions?: string[]): any[] {
  return arr.slice().sort((a, b) => {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const dir = (directions && directions[i] === 'desc') ? -1 : 1;
      const aVal = a[field];
      const bVal = b[field];
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
    }
    return 0;
  });
}

export function arraysEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length != b.length) return false;

  const aSorted = a.slice();
  aSorted.sort();
  const bSorted = b.slice();
  bSorted.sort();

  for (let i = 0; i < aSorted.length; ++i) {
    if (aSorted[i] !== bSorted[i]) return false;
  }
  return true;
}

export function getCircularReplacer() {
  const ancestors: any[] = [];

  return function (this: any, key: string, value: any) {
    if (value instanceof Window || value instanceof HTMLElement) {
      return undefined;
    }

    if (typeof value === "object" && value !== null) {
      while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
        ancestors.pop();
      }

      if (ancestors.indexOf(value) !== -1) {
        return undefined;
      }

      ancestors.push(value);
    }

    return value;
  };
}

export function deepCopyProperties(obj: any): any {
  if (!hasValue(obj)) {
    return null;
  }
  return JSON.parse(JSON.stringify(obj, getCircularReplacer()));
}

export function replaceObjectPropertyValuesWithValue(obj: Object, oldValue: any, newValue: any): void {
  for (var key in obj) {
    if (obj.hasOwnProperty(key) && (obj as any)[key] === oldValue) {
      (obj as any)[key] = newValue;
    }
  }
}

export function indexOfValueInArrayByProperty(arr: any, propName: string, propValue: any): number {
  if (!hasValue(arr)) {
    return -1;
  }
  const arrLength: number = arr.length;
  for (let i = 0; i < arrLength; i++) {
    const obj = arr[i];
    if (propValue === obj[propName]) {
      return i;
    }
  }
  return -1;
}

export function getObjectInArrayByProperty(arr: any, propName: string, propValue: any): any {
  const idx = indexOfValueInArrayByProperty(arr, propName, propValue);
  if (idx >= 0) {
    return arr[idx];
  }
  return null;
}

export function isInArrayByProperty(arr: any, propName: string, propValue: any): boolean {
  return (indexOfValueInArrayByProperty(arr, propName, propValue) >= 0);
}

export function removeObjectFromArrayByProperty(arr: any, propName: any, propValue: any): void {
  const indexOfValueToRemove = indexOfValueInArrayByProperty(arr, propName, propValue);
  if (indexOfValueToRemove >= 0) {
    arr.splice(indexOfValueToRemove, 1);
  }
}

export function isInArray(arr: any, elt: any): boolean {
  if (!hasValue(arr)) {
    return false;
  }
  for (let item of arr) {
    if (item === elt) {
      return true;
    }
  }
  return false;
}

export function isInArrayByCompareFunc(arr: any, compareFunc: Function): boolean {
  if (!hasValue(arr)) {
    return false;
  }
  const arrLength: number = arr.length;
  for (let i = 0; i < arrLength; i++) {
    const obj = arr[i];
    if (compareFunc(obj)) {
      return true;
    }
  }
  return false;
}

export function removeFromArray(arr: any, elt: any): void {
  const index = arr.indexOf(elt);
  if (index > -1) {
    arr.splice(index, 1);
  }
}

/**
 * Remove the first item from `arr` whose `id` property matches the given `id`.
 * Mutates the array in-place. Shorthand for `removeObjectFromArrayByProperty(arr, 'id', id)`.
 */
export function removeFromArrayById(arr: any[] | undefined | null, id: number | string): void {
  if (!arr) return;
  const index = arr.findIndex((item: any) => item.id === id);
  if (index > -1) {
    arr.splice(index, 1);
  }
}

export function cleanArray(actual: any): any[] {
  const newArray: any[] = [];
  for (let i = 0; i < actual.length; i++) {
    if (actual[i]) {
      newArray.push(actual[i]);
    }
  }
  return newArray;
}

export function difference(a: any, b: any): any[] {
  return a.filter((itema: any) => !b.some((itemb: any) => itemb === itema));
}

export function sortAndRemoveDuplicates(a: any, compareFunc: any): any[] {
  a.sort(compareFunc);
  for (var i = 1; i < a.length;) {
    if (compareFunc(a[i - 1], a[i]) === 0) {
      a.splice(i, 1);
    } else {
      i++;
    }
  }
  return a;
}

export function isArrayEmpty(arr: any): boolean {
  if (!hasValue(arr)) {
    return false;
  }
  return arr.length === 0;
}

export function isString(arg: any): boolean {
  return (typeof arg === 'string' || arg instanceof String);
}

export function generatePropertyToTrueObject(arr: Array<any>, prop: string): Record<string, boolean> {
  return arr.reduce(function (result: any, item: any) {
    result[item[prop]] = true;
    return result;
  }, {});
}

export function arraysEqualsWithIEquals(a: any[], b: any[]): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length != b.length) return false;

  const aSorted = a.slice();
  aSorted.sort();
  const bSorted = b.slice();
  bSorted.sort();

  for (let i = 0; i < aSorted.length; ++i) {
    if (aSorted[i].equals(bSorted[i])) return false;
  }
  return true;
}

export function runPromiseFactoriesSequentially(promiseFunction: Function, promiseParametersArray: Array<any>): Promise<any> {
  var result = Promise.resolve();
  promiseParametersArray.forEach(promiseParameters => {
    result = result.then(() => promiseFunction(...promiseParameters));
  });
  return result;
}
